import { DeleteOutlined } from '@ant-design/icons';
import { Fragment, PureComponent, ReactNode } from 'react';
import { TEETH_TO_NAME, allSurfacesIdToName } from '../constants';
import PadV2DentalExaminationTable from './DentalExaminationTable';
import { DentalExaminationOnPadAutoSuggest } from './DentalExaminationAutoSuggest';
import { DentalExamination as DentalExaminationType } from '../../../../../store/types/prescription';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../../store/reducers';
import { getChartType } from '../../../../../store/selectors/prescriptionPad';
import { ChartType } from '../DentalChart';
import { Group } from '../DentalExamination';

interface Props {
    teeth: string;
    surfaces: Set<string>;
    onTeethDelete: (teeth: string, group?: Group) => void;
    elements: DentalExaminationType[] | undefined;
    surfacesArray: {
        id: string;
        name: string;
    }[];
    onUpdate: () => void;
    chartType: ChartType;
    group?: Group;
    onTeethGroupEdit: () => void;
}

class DentalExaminationComponent extends PureComponent<Props> {
    render(): ReactNode {
        const {
            teeth,
            surfaces,
            onTeethDelete,
            elements,
            surfacesArray,
            onUpdate,
            chartType,
            group,
            onTeethGroupEdit,
        } = this.props;
        const groupData = group && group.data ? Object.entries(group.data) : [];

        return (
            <div className="pt-10" id={`teeth-${teeth}`}>
                <div className="bg-darwin-green-bg-light p-8 rounded-8 space-y-8 border border-darwin-neutral-100">
                    <div className="flex items-center justify-between overflow-hidden space-x-12">
                        {groupData.length > 0 ? (
                            <div className="flex-1 flex items-center space-x-8 whitespace-nowrap overflow-auto">
                                {groupData.map((i) => {
                                    const surfaces = Array.from(i[1])
                                        .map((i) => allSurfacesIdToName[i])
                                        .filter(Boolean);
                                    return (
                                        <div className="flex items-center space-x-4">
                                            <div>
                                                <img
                                                    src={`/images/dental-images/teeth-${i[0]}.webp`}
                                                    className="h-32 min-w-18"
                                                />
                                            </div>
                                            <p className="flex items-center space-x-5 text-white bg-darwin-green-primary rounded-4 font-700 px-10 py-4">
                                                <p>{TEETH_TO_NAME[teeth] || `T${i[0]}`}</p>
                                                {surfaces.length > 0 && (
                                                    <Fragment>
                                                        <p>:</p>
                                                        <p>{surfaces.join(', ')}</p>
                                                    </Fragment>
                                                )}
                                            </p>
                                        </div>
                                    );
                                })}
                                <div className="pl-4">
                                    <svg
                                        className="w-18 h-18 fill-darwin-neutrals-600 cursor-pointer"
                                        viewBox="0 0 17 18"
                                        xmlns="http://www.w3.org/2000/svg"
                                        onClick={onTeethGroupEdit}
                                    >
                                        <path d="M1.625 12.2812C1.75 11.8438 2 11.4375 2.3125 11.125L11.8125 1.625C12.5938 0.84375 13.875 0.84375 14.6562 1.625L15.875 2.84375C15.9688 2.9375 16.0625 3.0625 16.125 3.15625C16.6562 3.9375 16.5625 5 15.875 5.6875L6.375 15.1875C6.34375 15.2188 6.28125 15.25 6.25 15.3125C5.9375 15.5625 5.59375 15.75 5.21875 15.875L2.78125 16.5938L1.4375 17C1.1875 17.0625 0.90625 17 0.71875 16.7812C0.5 16.5938 0.4375 16.3125 0.53125 16.0625L0.90625 14.7188L1.625 12.2812ZM3.0625 12.7188L2.84375 13.4375L2.34375 15.1562L4.0625 14.6562L4.78125 14.4375C5 14.375 5.15625 14.2812 5.3125 14.125L12.4688 6.96875L10.5312 5.03125L3.375 12.1875C3.34375 12.1875 3.34375 12.2188 3.3125 12.25C3.1875 12.375 3.125 12.5312 3.0625 12.7188Z" />
                                    </svg>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-12">
                                <div>
                                    <img
                                        src={`/images/dental-images/teeth-${teeth}.webp`}
                                        className="h-32"
                                    />
                                </div>
                                <p className="text-white bg-darwin-green-primary rounded-4 font-700 px-10 py-4">
                                    {TEETH_TO_NAME[teeth] || `T${teeth}`}
                                </p>
                                <div className="flex items-center space-x-8">
                                    {Array.from(surfaces).map((i) => (
                                        <p
                                            className="text-darwin-neutral_new-800 rounded-4 px-10 py-4"
                                            style={{ background: '#E8E8E8' }}
                                        >
                                            {allSurfacesIdToName[i]}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div>
                            <DeleteOutlined
                                style={{ color: '#616A8F', marginRight: '8px' }}
                                size={24}
                                onClick={() => onTeethDelete(teeth, group)}
                                onPointerLeaveCapture={undefined}
                            />
                        </div>
                    </div>
                    {Array.isArray(elements) && (
                        <PadV2DentalExaminationTable
                            teeth={teeth}
                            elements={elements}
                            surfaces={surfacesArray}
                            onUpdate={onUpdate}
                            chartType={chartType}
                            group={group}
                        />
                    )}
                    <DentalExaminationOnPadAutoSuggest
                        teeth={teeth}
                        surfaces={surfacesArray}
                        group={group}
                    />
                </div>
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({ chartType: getChartType(state) }))(
    DentalExaminationComponent,
);
